import { useEffect, useState } from 'react';
import { Button, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import EnhetMatchLookupApi from 'services/KjederegisteretAdminApi/EnhetMatchLookupApi';
import {
  EnhetLookupContext,
  EnhetLookupStepName,
  LookupCandidate,
} from 'services/KjederegisteretAdminApi/EnhetMatchLookupApi/types';
import EnhetKildeApi from 'services/KjederegisteretAdminApi/EnhetKildeApi';
import {
  KILDE_LABELS,
  buildKildeLink,
} from 'services/KjederegisteretAdminApi/EnhetKildeApi/constants';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';

interface Props {
  stepName: EnhetLookupStepName;
  context: EnhetLookupContext;
  enhetId: number;
  currentUser: string;
  onLinked: () => void;
  onResult?: (hasCandidates: boolean) => void;
}

// Proactively searches and, only once results exist, renders as a compact badge - clicking opens
// a popover to pick and link a candidate. Renders nothing while searching or when empty, so
// several of these can sit side by side without reserving layout space for a source that found
// nothing (a search returning many loosely-related results, e.g. Google Places in a small town,
// shouldn't force a wall of always-expanded buttons onto the page).
const KildeSuggestionInline = (props: Props) => {
  const toaster = useToaster();
  const isMountedRef = useIsMountedRef();
  const [candidates, setCandidates] = useState<LookupCandidate[]>([]);
  const [linkingId, setLinkingId] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    EnhetMatchLookupApi.findCandidates(props.stepName, props.context)
      .then((result) => {
        if (!cancelled) {
          setCandidates(result);
          props.onResult?.(result.length > 0);
        }
      })
      .catch(() => {
        // Proactive background check - fail silently, also covers a source's API key not being configured
        if (!cancelled) props.onResult?.(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.stepName, props.enhetId]);

  const handleLink = async (candidate: LookupCandidate) => {
    setLinkingId(candidate.id);
    try {
      const result = await EnhetMatchLookupApi.confirm(props.stepName, props.context, candidate.id);
      await EnhetKildeApi.tilknytt({
        enhetId: props.enhetId,
        kilde: props.stepName,
        eksternId: result.details.id,
        visningsnavn: result.details.displayName,
        tilknyttetAv: props.currentUser,
      });
      toaster.success('Kilde tilknyttet', `${KILDE_LABELS[props.stepName]} er nå tilknyttet.`);
      if (isMountedRef.current) setCandidates([]);
      props.onLinked();
    } catch (err) {
      toaster.error('Noe gikk galt', HttpErrorHelper.formatError(err));
    } finally {
      if (isMountedRef.current) setLinkingId(undefined);
    }
  };

  if (candidates.length === 0) return null;

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom-start"
      rootClose
      overlay={
        <Popover style={{ maxWidth: 380 }}>
          <Popover.Header>{KILDE_LABELS[props.stepName] ?? props.stepName}</Popover.Header>
          <Popover.Body style={{ maxHeight: 320, overflowY: 'auto' }}>
            {candidates.map((c) => {
              const sourceLink = buildKildeLink(props.stepName, c.id);
              return (
                <div
                  key={c.id}
                  className="d-flex justify-content-between align-items-center mb-2 gap-2"
                >
                  <div className="text-truncate">
                    <div className="fw-semibold text-truncate">
                      {c.displayName}
                      {sourceLink && (
                        <>
                          {' '}
                          <a
                            href={sourceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted"
                            title="Åpne kilden"
                          >
                            <small>↗</small>
                          </a>
                        </>
                      )}
                    </div>
                    {(c.fields.adresse ?? c.fields.gateadresse ?? c.fields.poststed) && (
                      <small className="text-muted text-truncate d-block">
                        {[c.fields.adresse ?? c.fields.gateadresse, c.fields.poststed]
                          .filter(Boolean)
                          .join(', ')}
                      </small>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={!!linkingId}
                    onClick={() => handleLink(c)}
                    className="flex-shrink-0"
                  >
                    {linkingId === c.id ? <Spinner animation="border" size="sm" /> : 'Koble til'}
                  </Button>
                </div>
              );
            })}
          </Popover.Body>
        </Popover>
      }
    >
      <span
        role="button"
        className="badge rounded-pill bg-info text-dark"
        style={{ cursor: 'pointer', fontSize: '0.75em' }}
      >
        {KILDE_LABELS[props.stepName] ?? props.stepName} ({candidates.length})
      </span>
    </OverlayTrigger>
  );
};

export default KildeSuggestionInline;
